import * as React from 'react';
import {IMT} from '@practicegenius/pg-util/src/StringUtils';
import {isCardNumber} from '@practicegenius/pg-util/src/CardUtils';
import * as registrationMachine from '../machines/accountRegistration';
import {
  primaryActionButtonProps,
  PrimaryActionButtonProps,
  secondaryActionButtonProps,
  SecondaryActionButtonProps,
} from './utils';
import lang from '../lang';
import {UseMachineType} from '../machines/useAccountRegistrationMachineType';

type Props = {
  registrationMachine: UseMachineType;
};

type ScreenProps = Props & {
  lang: typeof lang.nameMismatch;
  title: string;
  instructions: string;
  cardNumber: string | null;
  linkedPractice: {name: string} | null;
  actionButtons: {
    primary: PrimaryActionButtonProps;
    secondary: SecondaryActionButtonProps;
  };
};

export type NameMismatchScreenType = React.ComponentType<ScreenProps>;

const getTitle = (
  cardNumber: string | null,
  inviteCode: string | null
): string => {
  const method = (() => {
    switch (true) {
      case Boolean(inviteCode):
        return lang.nameMismatch.title.methods.inviteCode;
      case cardNumber && isCardNumber(cardNumber):
        return lang.nameMismatch.title.methods.cardNumber;
      default:
        return lang.nameMismatch.title.methods.tempRegCode;
    }
  })();

  return IMT(lang.nameMismatch.title.template, {method});
};

const getInstructions = (
  cardNumber: string | null,
  inviteCode: string | null
): string => {
  if (inviteCode) {
    return lang.nameMismatch.instructions.inviteCode;
  }

  return cardNumber && isCardNumber(cardNumber)
    ? lang.nameMismatch.instructions.cardNumber
    : lang.nameMismatch.instructions.tempRegCode;
};

export const NameMismatch = (
  props: Props & {children: NameMismatchScreenType}
) => {
  const [{context}, send] = props.registrationMachine;

  const renderProps: ScreenProps = {
    ...props,
    lang: lang.nameMismatch,
    title: getTitle(context.cardNumber, context.inviteCode),
    instructions: getInstructions(context.cardNumber, context.inviteCode),
    cardNumber: context.cardNumber,
    linkedPractice: context.linkedPractice as null | {name: string},
    actionButtons: {
      primary: primaryActionButtonProps({
        onClick: () => {
          send(registrationMachine.RegistrationEvent.RETRY_NAME_VERIFICATION);
        },
        children: lang.nameMismatch.buttons.primary.title,
      }),
      secondary: secondaryActionButtonProps({
        children: lang.nameMismatch.buttons.secondary.title,
      }),
    },
  };

  const Screen = props.children;
  return <Screen {...renderProps} />;
};
